import { useEffect, useRef } from 'react';
import type { AccountThreadLightView } from '@shared/types/thread-work';

const MAX_TIMEOUT_MS = 2_147_000_000;

export const getProposalExpirySignature = (
  threadStates: Array<AccountThreadLightView | null | undefined>,
): string => threadStates
  .flatMap((threadState) => {
    if (!threadState) return [];
    return [threadState.currentTopic, ...threadState.otherTopics]
      .map((topic) => topic?.proposal?.expiresAt ?? null)
      .filter((expiresAt): expiresAt is string => Boolean(expiresAt));
  })
  .sort()
  .join('|');

/** Refreshes the server projection when an active proposal reaches its own
 * validity deadline. Business due dates never enter this timer. */
export const useProposalExpiryRefresh = ({
  threadStates,
  onRefresh,
}: {
  threadStates: Array<AccountThreadLightView | null | undefined>;
  onRefresh: () => void;
}) => {
  const signature = getProposalExpirySignature(threadStates);
  const refreshRef = useRef(onRefresh);

  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    const expiries = signature
      .split('|')
      .map((expiresAt) => Date.parse(expiresAt))
      .filter((expiresAt) => Number.isFinite(expiresAt) && expiresAt > Date.now());
    if (expiries.length === 0) return undefined;

    const nearest = Math.min(...expiries);
    let timer: number | null = null;
    const schedule = () => {
      const remaining = nearest - Date.now();
      if (remaining <= 0) {
        refreshRef.current();
        return;
      }
      timer = window.setTimeout(schedule, Math.min(remaining + 25, MAX_TIMEOUT_MS));
    };
    schedule();

    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [signature]);
};
